<script setup lang="ts">
/**
 * Мета-панель задачи — макет 13:463: колонка 280 с левой рамкой, подписи
 * 11px и значения 13px под ними.
 *
 * Поля правятся на месте: статус, исполнитель, приоритет и срок. Открывать
 * ради смены приоритета отдельную форму — это два лишних экрана на действие,
 * которое делают между делом.
 *
 * Эпик здесь ссылка, а не выбор: сменить родителя значит переложить задачу в
 * другую ветку иерархии, и это делается вместе со связями — DEV-1472.
 *
 * «Оценки» из макета здесь нет и не будет: её нет в модели задачи — ни поля,
 * ни ручки (docs/design-gaps.md).
 */
import { AppAvatar, AppAvatarGroup, AppDatePicker } from '@vinprop/design-system'
import { Paperclip } from 'lucide-vue-next'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import InlinePicker from '@/components/InlinePicker.vue'
import LabelPicker from './LabelPicker.vue'
import { PRIORITIES } from '@/features/issues/api'
import IssueTypeIcon from '@/features/issues/components/IssueTypeIcon.vue'
import PriorityGlyph from '@/features/issues/components/PriorityGlyph.vue'
import StatusBadge from '@/features/issues/components/StatusBadge.vue'
import { formatStamp, toDateInput } from '@/features/issues/format'
import { useIssueStore } from '../store'

import type { V1Priority } from '@/api/clients/tracker-issue'

const emit = defineEmits<{ failed: [error: unknown] }>()

const { t, locale } = useI18n()
const store = useIssueStore()

const statusOptions = computed(() =>
  store.columns.map((column) => ({ value: column.id, label: column.name })),
)

const assigneeOptions = computed(() => [
  { value: '', label: t('issues.list.noAssignee') },
  ...store.members.filter((member) => member.name).map((m) => ({ value: m.id, label: m.name })),
])

const priorityOptions = computed(() =>
  PRIORITIES.map((value) => ({ value, label: t(`issues.priorities.${value}`) })),
)

/**
 * Наблюдатели показаны аватарами: в макете это ряд лиц, а не список имён.
 * Кто не участник проекта — тот и не наблюдатель, поэтому имя всегда есть.
 */
const watchers = computed(() =>
  (store.issue?.watcherIds ?? []).map((id) => ({
    name: store.members.find((member) => member.id === id)?.name || id,
  })),
)

const stamp = (iso: string) =>
  formatStamp(iso, locale.value, { today: t('issue.today'), yesterday: t('issue.yesterday') })

const dueValue = computed(() => toDateInput(store.issue?.dueDate ?? ''))

/** Отказ показывает страница: поле уже вернулось к прежнему значению */
const guard = (run: () => Promise<void>) => {
  run().catch((failure: unknown) => emit('failed', failure))
}

const onAssign = (assigneeId: string) => {
  const member = store.members.find((entry) => entry.id === assigneeId)

  guard(() => store.setAssignee(assigneeId, member?.name ?? ''))
}

const onDue = (value: string) => {
  guard(() => store.setDueDate(value))
}
</script>

<template>
  <aside v-if="store.issue" class="meta">
    <div class="mfield">
      <span class="mfield__label">{{ t('issue.meta.status') }}</span>
      <InlinePicker
        :options="statusOptions"
        :model-value="store.issue.columnId"
        :empty-text="t('toolbar.filters.empty')"
        @update:model-value="guard(() => store.setColumn($event))"
      >
        <template #trigger="{ toggle }">
          <button type="button" class="mfield__inline" @click="toggle()">
            <StatusBadge :name="store.column?.name ?? '—'" :color="store.column?.color ?? ''" />
          </button>
        </template>
      </InlinePicker>
    </div>

    <div class="mfield">
      <span class="mfield__label">{{ t('issue.meta.assignee') }}</span>
      <InlinePicker
        :options="assigneeOptions"
        :model-value="store.issue.assigneeId"
        :empty-text="t('issues.create.noMembers')"
        @update:model-value="onAssign"
      >
        <template #trigger="{ toggle }">
          <button type="button" class="mfield__inline mfield__row" @click="toggle()">
            <AppAvatar :name="store.issue.assigneeName" :size="24" />
            <span class="mfield__value">
              {{ store.issue.assigneeName || t('issues.list.noAssignee') }}
            </span>
          </button>
        </template>
      </InlinePicker>
    </div>

    <div class="mfield">
      <span class="mfield__label">{{ t('issue.meta.priority') }}</span>
      <InlinePicker
        :options="priorityOptions"
        :model-value="store.issue.priority"
        :empty-text="t('toolbar.filters.empty')"
        @update:model-value="guard(() => store.setPriority($event as V1Priority))"
      >
        <template #trigger="{ toggle }">
          <button type="button" class="mfield__inline mfield__row mfield__row--tight" @click="toggle()">
            <!-- У глифа своя скрытая подпись, а рядом стоит видимая: без
                 aria-hidden скринридер читает приоритет дважды. -->
            <PriorityGlyph :priority="store.issue.priority" aria-hidden="true" />
            <span class="mfield__value">
              {{
                store.issue.priority
                  ? t(`issues.priorities.${store.issue.priority}`)
                  : t('issues.list.noPriority')
              }}
            </span>
          </button>
        </template>
      </InlinePicker>
    </div>

    <!-- Срок — единственное поле, которое рисует не текст, а поле-календарь
         дизайн-системы: своя панель с сеткой месяца была бы вторым таким
         календарём в кодовой базе (docs/design-gaps.md). -->
    <div class="mfield">
      <span class="mfield__label">{{ t('issue.meta.due') }}</span>
      <AppDatePicker :model-value="dueValue" @update:model-value="onDue" />
    </div>

    <div class="mfield">
      <span class="mfield__label">{{ t('issue.meta.labels') }}</span>
      <LabelPicker @failed="emit('failed', $event)" />
    </div>

    <div v-if="watchers.length" class="mfield">
      <span class="mfield__label">{{ t('issue.meta.watchers') }}</span>
      <AppAvatarGroup :users="watchers" :size="24" :max="5" />
    </div>

    <!-- Файлы здесь только счётчиком: сами плитки стоят под описанием, и
         второй их список в узкой колонке был бы тем же самым дважды. -->
    <div v-if="store.attachments.length" class="mfield">
      <span class="mfield__label">{{ t('issue.attachments.title') }}</span>
      <span class="mfield__row mfield__value">
        <Paperclip class="mfield__clip" :size="14" />
        {{ t('issue.attachments.count', store.attachments.length) }}
      </span>
    </div>

    <div v-if="store.issue.parentKey" class="mfield">
      <span class="mfield__label">{{ t('issue.meta.epic') }}</span>
      <RouterLink class="mfield__row mfield__row--tight mfield__link" :to="`/issue/${store.issue.parentKey}`">
        <IssueTypeIcon type="ISSUE_TYPE_EPIC" />
        {{ store.issue.parentKey }}
      </RouterLink>
    </div>

    <div v-if="store.project" class="mfield">
      <span class="mfield__label">{{ t('issue.meta.project') }}</span>
      <RouterLink class="mfield__row mfield__project" :to="`/p/${store.project.key}/board`">
        <span
          class="mfield__dot"
          :style="{ background: store.project.color || 'var(--color-primary-light)' }"
        />
        {{ store.project.name }} · {{ store.project.key }}
      </RouterLink>
    </div>

    <div class="meta__divider" />

    <p class="meta__stamp">
      {{
        store.issue.reporterName
          ? t('issue.meta.createdBy', {
              date: stamp(store.issue.createdAt),
              name: store.issue.reporterName,
            })
          : t('issue.meta.created', { date: stamp(store.issue.createdAt) })
      }}
    </p>
    <p class="meta__stamp">
      {{ t('issue.meta.updated', { date: stamp(store.issue.updatedAt) }) }}
    </p>
  </aside>
</template>

<style scoped>
.meta {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 20px;
  width: 280px;
  padding: 32px 24px;
  border-left: 1px solid var(--color-border);
  background: var(--color-surface);
}

.mfield {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
}

/* Высоты строк здесь заданы явно: в макете блок поля ровно 38 (подпись 13,
   отступ 6, значение 19), и от наследованной высоты строки он вырастает на
   шесть пикселей — на девяти полях это уже полсотни. */
.mfield__label {
  color: var(--color-text-muted);
  font-size: var(--font-size-overline);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.6px;
  line-height: 13px;
  text-transform: uppercase;
}

/* Триггеры не выглядят кнопками: в макете это само значение поля */
.mfield__inline {
  display: inline-flex;
  padding: 0;
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}

.mfield__inline:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

.mfield__row {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.mfield__row--tight {
  gap: 6px;
}

.mfield__value {
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  font-weight: var(--font-weight-medium);
  line-height: 16px;
}

.mfield__link {
  color: var(--color-primary);
  font-size: var(--font-size-body-sm);
  font-weight: var(--font-weight-medium);
  line-height: 16px;
  text-decoration: none;
}

.mfield__project {
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  line-height: 16px;
  text-decoration: none;
}

.mfield__clip {
  flex-shrink: 0;
  color: var(--color-text-muted);
}

.mfield__dot {
  width: 8px;
  height: 8px;
  border-radius: var(--radius-full);
}

.meta__divider {
  height: 1px;
  background: var(--color-border);
}

/* Шаг между строками — 33, как в макете: 13 на строку и 20 на просвет */
.meta__stamp {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-overline);
  line-height: 13px;
}
</style>
